import type { QuestFormState } from '../questFormData'
import { LAB_ACCOUNT_SYSTEMS } from '../formData'
import type { OrderPdfData, OrderPdfTest, OrderPdfSection, AutoPopulateData } from './pdfTypes'
import type { Facility } from '../shared/facilityTypes'
import type { CatalogTest } from '../shared/useTestCatalog'
import { buildPatientSelectedLocationPdfFields } from './patientSelectedLocation'

export function mapQuestToPdf(
  form: QuestFormState,
  auto: AutoPopulateData,
  selectedFacility?: Facility,
  testMap?: Map<string, CatalogTest>,
  logoDataUrl?: string,
  /** PD-1312 — patient will choose any Quest location; nothing was faxed. */
  patientSelectedLocation?: boolean,
): OrderPdfData {
  // Resolve selected tests from the catalog map
  const selectedTests: OrderPdfTest[] = form.selectedTests.map((code) => {
    const test = testMap?.get(code)
    return {
      code,
      name: test?.name || code,
      category: test?.categoryDisplay || test?.category || 'General',
    }
  })

  // Write-in tests
  for (const writeIn of form.writeInTests) {
    if (writeIn.testNumber || writeIn.testName) {
      selectedTests.push({
        code: writeIn.testNumber || 'N/A',
        name: writeIn.testName || 'Custom Test',
        category: 'Write-In',
      })
    }
  }

  const vendorSections: OrderPdfSection[] = []

  // STAT flag
  if (form.isStat) {
    vendorSections.push({ title: 'Priority', type: 'list', items: ['STAT'] })
  }

  // Microbiology sources
  if (form.microbiologySources.length > 0 || form.microbiologyOtherSource) {
    const sources = [...form.microbiologySources]
    if (form.microbiologyOtherSource) sources.push(`Other: ${form.microbiologyOtherSource}`)
    vendorSections.push({ title: 'Microbiology Specimen Sources', type: 'list', items: sources })
  }

  // Collection details
  const collectionData: Record<string, string> = {}
  if (form.collectionDate) collectionData['Collection Date'] = form.collectionDate
  if (form.collectionTime)
    collectionData['Collection Time'] = `${form.collectionTime} ${form.collectionAmPm}`
  if (form.isFasting) collectionData['Fasting'] = 'Yes'
  if (Object.keys(collectionData).length > 0) {
    vendorSections.push({ title: 'Collection Details', type: 'keyValue', data: collectionData })
  }

  // Clinical comments
  if (form.clinicalComments) {
    vendorSections.push({
      title: 'Clinical Comments',
      type: 'text',
      content: form.clinicalComments,
    })
  }

  // ICD-10 from diagnosis text
  const icd10Codes = form.diagnosisText
    ? form.diagnosisText
        .split(/[;\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
    : []

  return {
    vendorName: 'Quest Diagnostics',
    orderTitle: 'General Requisition',
    patient: {
      firstName: auto.patient.firstName,
      middleName: auto.patient.middleName,
      lastName: auto.patient.lastName,
      dateOfBirth: auto.patient.dateOfBirth,
      sex: auto.patient.sexCode,
      email: auto.patient.email,
      phone: auto.patient.phone,
      address: auto.patient.address,
      city: auto.patient.city,
      state: auto.patient.state,
      zip: auto.patient.zip,
      country: auto.patient.country,
      patientId: auto.patient.patientId,
    },
    provider: {
      organizationName: auto.provider.organizationName,
      providerName: auto.provider.providerName,
      npi: auto.provider.npi,
      phone: auto.provider.orgPhone,
      fax: auto.provider.orgFax,
      address: auto.provider.orgAddress,
      city: auto.provider.orgCity,
      state: auto.provider.orgState,
      zip: auto.provider.orgZip,
      email: auto.provider.providerEmail,
      accountNumber: auto.provider.labAccountNumbers[LAB_ACCOUNT_SYSTEMS.QUEST] || '',
    },
    insurance: {
      primaryCarrier: auto.insurance.primaryCarrier,
      primaryMemberId: auto.insurance.primaryMemberId,
      primaryGroup: auto.insurance.primaryGroup,
      policyholderName: auto.insurance.policyholderName,
      relationship: auto.insurance.relationship,
    },
    icd10Codes,
    selectedTests,
    vendorSections,
    signatureDataUrl: form.signatureDataUrl,
    signatureDate: form.signatureDate,
    facilityHeader: selectedFacility
      ? {
          name: selectedFacility.name,
          address: `${selectedFacility.address}, ${selectedFacility.city}, ${selectedFacility.state} ${selectedFacility.zip}`,
          fax: selectedFacility.fax || 'N/A',
          logoDataUrl,
        }
      : undefined,
    ...(patientSelectedLocation
      ? buildPatientSelectedLocationPdfFields('Quest Diagnostics', auto)
      : undefined),
  }
}
